import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AutocompleteOptions,
  AutocompleteResponse,
  DocumentTypeWithLiterals,
  SearchOptions,
  SearchResponse,
  SiteDocumentSearch,
  SuggestOptions,
  SuggestResponse,
  autocomplete as universalAutocomplete,
  search as universalSearch,
  suggest as universalSuggest,
} from './search-platformized-v1-site-document-wix-site-search.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/search' };

export function search(httpClient: HttpClient): SearchSignature {
  return (
    search: SiteDocumentSearch,
    options?: NonNullablePaths<SearchOptions, `documentType`, 2>
  ) =>
    universalSearch(
      search,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchSignature {
  /**
   * Retrieves a list of site documents that match the provided search query and optionally performs aggregations on the data queried.
   *
   * The `search()` API supports the document types listed in the [Introduction](https://dev.wix.com/docs/sdk/backend-modules/search/wix-site-search/introduction), each with its own schema. These schemas define the fields available for filtering, sorting, and free-text searching.
   *
   * To learn more about working with the search query, see [API Query Language](https://dev.wix.com/docs/sdk/articles/working-with-the-sdk/api-query-language).
   * @param - Search query and aggregation information.
   */
  (
    search: SiteDocumentSearch,
    options?: NonNullablePaths<SearchOptions, `documentType`, 2>
  ): Promise<
    NonNullablePaths<
      SearchResponse,
      | `siteDocumentItems`
      | `siteDocumentItems.${number}._id`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

/** @internal */
export function autocomplete(httpClient: HttpClient): AutocompleteSignature {
  return (
    query: string,
    documentType: DocumentTypeWithLiterals,
    options?: AutocompleteOptions
  ) =>
    universalAutocomplete(
      query,
      documentType,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AutocompleteSignature {
  /**
   * Provides phrase completion. For example "blu" could return "blue", "blues" and "blunt" as candidate phrases.
   * This operation is resource heavy at index time and is reserved for special use cases.
   * @param - Query phrase to fetch completions for.
   * @param - Document type to use to search for phrases.
   */
  (
    query: string,
    documentType: DocumentTypeWithLiterals,
    options?: AutocompleteOptions
  ): Promise<
    NonNullablePaths<
      AutocompleteResponse,
      `values` | `values.${number}.query`,
      4
    >
  >;
}

/** @internal */
export function suggest(httpClient: HttpClient): SuggestSignature {
  return (
    documentType: DocumentTypeWithLiterals,
    options?: NonNullablePaths<SuggestOptions, `suggest`, 2>
  ) =>
    universalSuggest(
      documentType,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SuggestSignature {
  /**
   * Executes search query to fetch suggested items. Unlike search query, suggest will match
   * partial phrases (for example "blu" will match documents containing "blue", "blues" and "blunt").
   * Phrase needs to be at least 3 symbols long. Suggestions can also perform optimizations in search
   * results and generally do not guarantee the same level of quality as regular Search endpoint.
   * @param - Document type of documents to search for.
   */
  (
    documentType: DocumentTypeWithLiterals,
    options?: NonNullablePaths<SuggestOptions, `suggest`, 2>
  ): Promise<
    NonNullablePaths<
      SuggestResponse,
      `siteDocumentItems` | `siteDocumentItems.${number}._id`,
      4
    >
  >;
}

export {
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  AutocompleteOptions,
  AutocompleteRequest,
  AutocompleteResponse,
  AutocompleteResponseValue,
  DocumentType,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  Paging,
  PagingMetadata,
  Results,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  Search,
  SearchDetails,
  SearchOptions,
  SearchPagingMethodOneOf,
  SearchRequest,
  SearchResponse,
  SearchResponsePagingOneOf,
  SiteDocument,
  SiteDocumentSearchSpec,
  SortOrder,
  Sorting,
  Suggest,
  SuggestDetails,
  SuggestOptions,
  SuggestPagingMethodOneOf,
  SuggestRequest,
  SuggestResponse,
  ValueAggregation,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  utils,
} from './search-platformized-v1-site-document-wix-site-search.universal.js';
